import PreloadImage from "@/components/AsyncImage";
import BlueSection from "@/components/BlueSection";
import { BuyOverlay } from "@/components/BuyOverlay";
import { CalendarModalHeadless } from "@/components/CalendarModal";
import { CyanSection } from "@/components/CyanSection";
import { PageBase } from "@/components/PageBase";
import { WhiteSection } from "@/components/WhiteSection";
import dayjs from "@/utils/dayjs";
import { animate, motion } from "motion/react";
import { useEffect, useRef } from "react";
import styled from "styled-components";
import { useMediaQuery } from "usehooks-ts";
import { Footer } from "../components/Footer";
import { Navbar } from "../components/Navbar";
import GreenSection from "@/components/GreenSection";
import { ProgressiveBanner } from "@/components/ProgressiveBanner";
import { backgroundHomePlaceholder } from "@/generated/placeholders/background-home";
import { backgroundHomePlaceholderMobile } from "@/generated/placeholders/background-home-mobile";

const Container = styled(PageBase)`
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    z-index: 2;

    .absolute-elements {
        position: relative;
        inset: 0;
        height: 100vh;
        width: 100%;
        z-index: -1;
        display: flex;
        justify-content: center;
        align-items: center;
        overflow: visible;
        pointer-events: none;

        > * {
            pointer-events: none;
        }

        .background {
            z-index: 1 !important;
        }
    }

    .logo-wrapper {
        position: absolute;
        width: 50rem;
        height: 25rem;
        z-index: 6;
        pointer-events: none;

        img {
            position: relative;
            bottom: 50px;
            height: 18rem;
            z-index: 5;
        }
    }

    .character-wrapper {
        position: absolute;
        top: 0;

        img {
            position: relative;
            right: -470px;
            width: 100%;
            height: 100%;
            width: 40rem;
            height: 40rem;
            z-index: 4;
            aspect-ratio: 1/1;
        }
    }
    img.bee {
        position: absolute;
        width: 6rem;
        height: 6rem;
    }

    img.smurf-3 {
        position: absolute;
        left: -17rem;
        z-index: 1;

        width: 50rem;
    }

    .calendar-section {
        position: relative;
        display: flex;
        justify-content: center;
        padding: 4rem 2rem;

        backdrop-filter: blur(10px);
        background: white;

        .loading-box {
            position: relative;
            border-radius: 1rem;

            .calendar-wrapper {
                background-color: #fff;
                padding: 2rem;
                border-radius: 1rem;
                width: 30rem;
            }
        }

        .character-3 {
            position: absolute;
            width: 19rem;
            bottom: -5rem;
            left: 19rem;

            @media (max-width: 1200px) {
                left: 9rem;
                width: 16rem;
            }

            @media (max-width: 800px) {
                display: none;
            }
        }

        .character-4 {
            position: absolute;
            width: 29rem;
            bottom: -5rem;
            right: 12rem;

            @media (max-width: 1200px) {
                right: 3rem;
                width: 25rem;
            }

            @media (max-width: 800px) {
                display: none;
            }
        }

        &::before {
            content: "";
            position: absolute;
            width: 100%;
            height: 100%;
            inset: 0;
            background-image: ${() =>
                `url("/home/calendar-banner.png")`};
            background-size: cover;
            background-position-y: -15rem;
            background-repeat: no-repeat;
            z-index: -1;
            opacity: 0.8;

            @media screen and (max-width: 1550px) {
                background-position-y: center;
            }
        }
    }

    @media (max-width: 1000px) {
        .absolute-elements {
            height: 60vh;
        }

        img.smurf-3 {
            display: none;
        }

        img.bee {
            display: none;
        }

        .character-wrapper {
            right: 350px;
            top: 0;
            left: auto;

            img {
                position: relative;
                right: -470px;
                width: 40rem;
                height: 40rem;
            }
        }

        .logo-wrapper {
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            width: min(40rem, 94vw);
            height: auto;
            margin: 0;

            img {
                position: relative;
                bottom: 2rem;
                height: 13.5rem;
                width: auto;
                z-index: 5;
            }
        }
    }

    @media (max-width: 600px) {
        .character-wrapper img {
            width: 41rem;
            height: 41rem;
        }

        .logo-wrapper {
            width: min(34rem, 96vw);

            img {
                height: 10.5rem;
                bottom: 1rem;
            }
        }
    }
`;

const BeeBase = styled(motion.img)`
    display: flex;
    z-index: 8 !important;
    position: absolute !important;
    right: 0;
    height: 8rem;
    width: 8rem;
    top: 30%;
`;

export const HomePage = () => {
    const isMobile = useMediaQuery("(max-width: 1000px)", {
        initializeWithValue: true,
    });
    const intervalRef = useRef<Timer | null>(null);

    useEffect(() => {
        if (isMobile) return;

        const duration = 10;
        const myAnimation = () => {
            animate(
                "#bee",
                {
                    right: ["-5%", "110%"],
                    y: [-55, 20, 55, 20, -55],
                    rotate: [5, -20, 5, -20, 5],
                },
                { duration, ease: "linear" }
            );
        };
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(myAnimation, duration * 1000 * 1.5);

        myAnimation();

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isMobile]);

    const calendarDate = dayjs();

    const minimumDate = dayjs("2025-12-01");

    const initialDate = calendarDate.isAfter(minimumDate)
        ? calendarDate
        : minimumDate;

    return (
        <Container>
            <Navbar />

            <div className="absolute-elements">
                <div className="logo-wrapper">
                    <motion.img
                        className="main-logo"
                        src="/logos/logo-nav.png"
                        alt="Vila dos Smurfs Park"
                        initial={
                            isMobile
                                ? { opacity: 0, y: -16 }
                                : { opacity: 0, scale: 0.9 }
                        }
                        animate={
                            isMobile
                                ? { opacity: 1, y: 0 }
                                : { opacity: 1, scale: 1 }
                        }
                        transition={
                            isMobile
                                ? { duration: 0.45, ease: [0.22, 1, 0.36, 1] }
                                : undefined
                        }
                    />
                </div>
                <ProgressiveBanner
                    className="background"
                    hdSrc={
                        isMobile
                            ? "/background-home-mobile.webp"
                            : "/background-home.png"
                    }
                    placeholderSrc={
                        isMobile
                            ? backgroundHomePlaceholderMobile
                            : backgroundHomePlaceholder
                    }
                    alt=""
                />
                <PreloadImage
                    src="/smurfs/home-3.png"
                    alt=""
                    className="smurf-3"
                    initial={{ y: 0, x: -400 }}
                    animate={{ y: 0, x: 0 }}
                    transition={{ delay: 2.5, duration: 0.5 }}
                />
                <div className="character-wrapper">
                    <PreloadImage
                        initial={
                            isMobile
                                ? { opacity: 0, y: 56, scale: 0.96 }
                                : {
                                      opacity: 1,
                                      y: -1000,
                                      x: -200,
                                      rotateZ: 40,
                                  }
                        }
                        animate={
                            isMobile
                                ? { opacity: 1, y: 0, scale: 1 }
                                : { opacity: 1, y: 0, x: 0, rotateZ: 0 }
                        }
                        transition={
                            isMobile
                                ? {
                                      delay: 0.15,
                                      duration: 0.55,
                                      ease: [0.22, 1, 0.36, 1],
                                  }
                                : { delay: 0.5, duration: 1.5 }
                        }
                        src="/smurfs/home.png"
                        className="character-1"
                    />
                </div>
                {!isMobile && (
                    <BeeBase
                        src="/bee.png"
                        className="bee"
                        id={"bee"}
                    />
                )}
            </div>
            <CyanSection />
            <div className="calendar-section" id="calendar-section">
                <div className="loading-box" style={{ zIndex: 2 }}>
                    <CalendarModalHeadless
                        initialDate={initialDate.toDate()}
                        className="calendar-wrapper"
                        callback={() => {}}
                        cartButton={true}
                        salesClosedPopup={true}
                        clearInitialDateWhenSalesClosed={true}
                    />
                </div>
                <BuyOverlay />
                <PreloadImage
                    initial={{ opacity: 1, y: 320 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.7 }}
                    src="/rules/smurf.png"
                    className="character-3"
                />
                <PreloadImage
                    initial={{ opacity: 1, y: 320, rotateY: 180 }}
                    whileInView={{ opacity: 1, y: 0, rotateY: 180 }}
                    transition={{ delay: 0.2, duration: 0.7 }}
                    src="/smurfs/faq.png"
                    className="character-4"
                />
            </div>
            <GreenSection />
            <BlueSection />
            <WhiteSection />
            <Footer />
        </Container>
    );
};
